import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './MailchimpSettingsModal.module.scss'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import {
  AudienceList,
  useMailchimpFetchAudienceLists,
} from 'hooks/queries/externalIntegrations/useMailchimpFetchAuidence'
import { useEffect, useState } from 'react'
import { SendToInput } from 'components/EmailBlock/inputs/SendToInput'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { MailchimpIntegration } from 'api/clients/ExternalIntegrationsClient'
import { MailchimpUser } from 'components/EmailBlock/types'

export const MailchimpSettingsModal = ({ integration }: { integration: MailchimpIntegration }) => {
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const updateExternalIntegration = useUpdateExternalIntegration()

  const {
    data: audienceLists,
    isLoading: audienceLoading,
    error: audienceError,
  } = useMailchimpFetchAudienceLists(workspaceId, true)
  const mailchimpProfile = integration.additional_data as MailchimpUser

  const [selectedContacts, setSelectedContacts] = useState<AudienceList | null>(null)

  useEffect(() => {
    if (!audienceLists || audienceLists.length === 0) return

    if (mailchimpProfile?.default_list_id) {
      const currentAudience = audienceLists.find((aud) => aud.id === mailchimpProfile.default_list_id)
      if (currentAudience) {
        setSelectedContacts(currentAudience)
        return
      }
    }

    setSelectedContacts(audienceLists[0])
  }, [audienceLists, mailchimpProfile?.default_list_id])

  const handleContactsChange = (contacts: AudienceList) => {
    setSelectedContacts(contacts)
  }

  const handleConfirm = async () => {
    if (!selectedContacts) {
      showToastMessage('Please select an audience list', 'error')
      return
    }

    try {
      await updateExternalIntegration.mutateAsync({
        id: integration.id,
        additional_data: {
          default_list_id: selectedContacts.id,
          default_from_name: mailchimpProfile.account_name,
        } as Partial<MailchimpUser>,
      })

      showToastMessage('Default audience list saved successfully!', 'success')

      closeModal()
    } catch (error) {
      showToastMessage('Failed to save default audience list. Please try again.', 'error')
    }
  }

  return (
    <Modal.Root size="sm" aria-label="Configure Mailchimp Integration">
      <Modal.Header title="Mailchimp Integration Settings" />
      <Modal.Content>
        <div className={Styles.content}>
          <Heading level={4}>Default recipients for all emails</Heading>
          <SendToInput
            audienceLoading={audienceLoading}
            audienceError={audienceError}
            audienceLists={audienceLists || []}
            selectedContacts={selectedContacts}
            onContactsChange={handleContactsChange}
            data-testid="send-to-input"
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleConfirm}
            isDisabled={!selectedContacts || updateExternalIntegration.isLoading}
          >
            Save Changes
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
